import React from "react";
import { Link } from "react-router-dom";
import { Flex, Tooltip } from "@fluentui/react-northstar";
import { PauseIcon, PlayIcon, CheckmarkCircleIcon, BanIcon, UrgentIcon } from "@fluentui/react-icons-northstar";
import IProject from "../interfaces/IProject";
import styles from "./projectItem.module.scss";
import { colorByTime, projectTimeToString, slaColor } from "../common/textOperations";
import { dueDateColor, parseDateToDateString } from "../common/dateOperations";
import { useApp } from "../contexts/appContext";
import ISLATimer from "../interfaces/ISLATimer";
import Countdown from "./countdown";

const titleByStatus = (code?: number): string => {
    switch (code) {
        case 100000000: return "New"
        case 1: return "In Progress";
        case 2: return "On Hold";
        case 3: return "Waiting for Details";
        case 5: return "Problem Solved";
        default: return "Canceled";
    }
}

const iconByStatus = (code?: number): JSX.Element => {
    switch (code) {
        case 100000000: return <PlayIcon outline />;
        case 1: return <PlayIcon />;
        case 2: return <PauseIcon />;
        case 3: return <PauseIcon outline />;
        case 5: return <CheckmarkCircleIcon outline />;
        default: return <BanIcon outline />;
    }
}

const ProjectItem: React.FunctionComponent<IProject> = ({ id, subject, dueDate, plannedHours, remainingHours, status, slaTimers }) => {
    const { state: appState } = useApp();
    const statusColor: string = colorByTime(plannedHours, remainingHours);
    const percent: number = ((plannedHours - remainingHours) / plannedHours) * 100;
    const bgColor: string = percent <= 100 ? "#a6c3dc1e" : "#ff002f13";
    const activeSLAs: ISLATimer[] = !!slaTimers ? slaTimers.filter((x: ISLATimer) => !x.terminalStateReached) : [];
    const sla: ISLATimer | null = activeSLAs.length > 0 ? activeSLAs[0] : null;
    const slaColorString: string = !sla ? "" : slaColor(sla.status, sla.terminalStateReached, sla.warningTime, sla.failureTime);

    return (
        <Link to={`/project/detail/${id}`} className={styles.itemContainer}
            style={{ color: appState.color, background: `linear-gradient(90deg, ${bgColor} ${percent}%, transparent 0%)` }}>
            <Flex gap="gap.small" fill>
                <div style={{ backgroundColor: statusColor }}>&nbsp;</div>
                <div className={styles.icon}>
                    <Tooltip trigger={iconByStatus(status)} content={titleByStatus(status)} />
                </div>
                <Flex column>
                    <span className={styles.title}>{subject}</span>
                    <span className={styles.subTitle}>
                        {projectTimeToString(plannedHours, remainingHours)}
                    </span>
                    {dueDate &&
                        <span className={styles.subTitle}>
                            Termín dokončení:{" "}
                            <span style={{ color: status != 5 ? dueDateColor(dueDate) : undefined }}>
                                {parseDateToDateString(dueDate)}
                            </span>
                        </span>
                    }
                </Flex>
                <Flex.Item push>
                    <Flex gap="gap.medium" vAlign="center" className={styles.rightSide}>
                        {activeSLAs.length > 0 &&
                            <Tooltip content="Aktivní SLA" position-align="before-center" trigger={
                                <Flex column gap="gap.smaller" className={styles.sla} style={{ color: slaColorString }}>
                                    <UrgentIcon />
                                    <small><Countdown endtime={activeSLAs[0].failureTime} /></small>
                                </Flex>
                            } />
                        }
                    </Flex>
                </Flex.Item>
            </Flex>
        </Link>
    );
};

export default ProjectItem;
